import { useTranslation } from "react-i18next";
import { Consecutive } from "../../../../../interfaces/Commons/consecutives";
import GenericPromises from "../../../../../api/GenericPromises";
import { useBase64 } from "../../../../../hooks/useBase64";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { AlertColor } from "@mui/material";
import DataTable from "../../../../../components/Tables/GridTableMaterialUI/DataTable";
import { GridColDef } from "@mui/x-data-grid";
import { Spinner } from "../../../../../components/Commons/Spinner/Spinner";
import { ButtonStyled } from "../../../../../theme/buttons";
import { Menuitem } from "../../../../../interfaces/Security/menu";
import { Resource } from "../../../../../interfaces/Security/resources";
import { DialogAddConsecutive } from "./consecutivesCompanyLocation/addConsecutives";
import { DialogUpdateConsecutive } from "./consecutivesCompanyLocation/updateConsecutives";

type TabCompanyLocationConsecutive = {
    company_location_id?: number;
    myConsecutives?: Consecutive[];
    setMyConsecutives?: Dispatch<SetStateAction<Consecutive[]>>
    showSnackBar: (message: string, severity: AlertColor) => void;
    resourceMenu: Menuitem | undefined;
}

export const ConsecutiveTab = ({
    company_location_id,
    myConsecutives,
    setMyConsecutives,
    showSnackBar,
    resourceMenu
}: TabCompanyLocationConsecutive) => {
    const [t] = useTranslation("global");
    const { utf8ToBase64Url } = useBase64();
    const { GenericGetResource, GenericPostResource, GenericPutResource } = GenericPromises();
    const [consecutiveData, setConsecutiveData] = useState<Consecutive[]>([]);
    const [myPreferences, setMyPreferences] = useState({});
    const [isLoadDataTable, setIsLoadDataTable] = useState(false);
    const [openDialogAdd, setOpenDialogAdd] = useState(false);
    const [openDialogUpdate, setOpenDialogUpdate] = useState(false);
    const [comboResources, setComboResources] = useState<Resource[]>([]);
    const [consecutivePayload, setConsecutivePayload] = useState<Consecutive>({})

    const [columnsConsecutives, setColumnsConsecutives] = useState<GridColDef<Consecutive>[]>([
        { field: 'name', headerName: t("consecutives.fields.name"), headerClassName: 'header-grid-table', flex: 1, },
        { field: 'description', headerName: t("consecutives.fields.description"), headerClassName: 'header-grid-table', flex: 1, },
        { field: 'prefix', headerName: t("consecutives.fields.prefix"), headerClassName: 'header-grid-table', flex: 1, },
        { field: 'resource_name', headerName: t("consecutives.fields.resource_name"), headerClassName: 'header-grid-table', flex: 1, },
        {
            field: 'creation_date', headerName: t("generic.creation_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
        },
        { field: 'last_update_user', headerName: t("generic.last_update_user"), headerClassName: 'header-grid-table', flex: 1 },
        {
            field: 'last_update_date', headerName: t("generic.last_update_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
        },
    ]);

    const loadPreferences = async () => {
        let myPreferences = await localStorage.getItem("grid-consecutives-companylocations");
        if (myPreferences != null) {
            setMyPreferences(JSON.parse(myPreferences));
        }
    }

    const loadColumnsOrder = async () => {
        let myOrderColumns = await localStorage.getItem("grid-consecutives-companylocations-columns");
        if (myOrderColumns != null) {
            let myJson = JSON.parse(myOrderColumns);
            for (let index = 0; index < myJson.length; index++) {
                const element = myJson[index];
                if (element['type'] === 'date' && (element.field === 'start_date' || element.field === 'end_date')) {
                    element.headerName = t(`consecutives.fields.${element.field}`);
                }
                else if (element['type'] === 'date') {
                    element.headerName = t(`generic.${element.field}`);
                }
                if (element.field) {
                }
                else {
                    element.headerName = t(`consecutives.fields.${element.field}`);
                }
            }
            setColumnsConsecutives(myJson);
        }
    }

    const onPost = (data: Consecutive) => {
        let myData: Consecutive = {
            name: data.name,
            description: data.description,
            prefix: data.prefix,
            resource_id: data.resource_id,
            resource_name: data.resource_name,
            view_name: data.view_name,
            length: data.length,
            consecutive: data.consecutive,
        };
        let myResource = comboResources.find((element) => data.resource_id = element.resource_id);
        if (setMyConsecutives) {
            if (setMyConsecutives) {
                setMyConsecutives((prev) => {
                    const lastId = prev.reduce((max, item) =>
                        item.consecutive_id && item.consecutive_id > max ? item.consecutive_id : max
                        , 0);

                    const newConsecutiveId = lastId + 1;

                    const newData = {
                        ...data,
                        consecutive_id: newConsecutiveId,
                    };

                    setConsecutiveData((old) => [...old, newData]);

                    return [...prev, newData];
                });
            }
        }
        else if (company_location_id) {
            myData = {
                ...myData,
                company_location_id: company_location_id,
            }
            GenericPostResource(`/consecutives`, myData).then((response) => {
                let myResponse = {
                    ...response.data,
                    resource_name: myResource?.resource_name,
                }
                setConsecutiveData((prev) => [...prev, myResponse]);
                showSnackBar(t("generic.messages.create"), "success");
                setOpenDialogAdd(false);
            }).catch((error) => {
                showSnackBar(error.message, "error");
                setOpenDialogAdd(false);
            })
        }
    }

    const onPut = (data: Consecutive) => {
        let myData = {};
        if (setMyConsecutives) {
            setMyConsecutives((prev) => [...prev, data]);
            setConsecutiveData((prev) => [...prev, myData]);
        }
        else if (company_location_id) {
            GenericPutResource(`/consecutives/${data.consecutive_id}`, myData).then((response) => {
                setConsecutiveData((prev) => [...prev, response.data.data]);
                showSnackBar(t("generic.messages.create"), "success");
                setOpenDialogAdd(false);
            }).catch((error) => {
                showSnackBar(error.message, "error");
                setOpenDialogAdd(false);
            })
        }
    }

    useEffect(() => {
        const loadData = async () => {
            if (myConsecutives) {
                setConsecutiveData(myConsecutives);
            }
            else {
                await GenericGetResource(`/consecutives?filter=${utf8ToBase64Url(`company_location_id=${company_location_id}`)}`).then((response) => {
                    setConsecutiveData(response.data.data);
                }).catch((error) => {
                    showSnackBar(error.message, "error");
                })
            }
            await GenericGetResource(`/resources`).then((response) => {
                setComboResources(response.data.data);
            }).catch((error) => {
                showSnackBar(error.message, "error");
            })
            loadColumnsOrder();
            loadPreferences();
            setIsLoadDataTable(true);
        }

        loadData()

    }, [])


    return (
        <>
            {!isLoadDataTable ?
                <Spinner /> : <>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <ButtonStyled
                            variant='outlined'
                            className="m-1" sx={{ color: 'primary.main' }}
                            disabled={(resourceMenu) ? !resourceMenu?.create : true}
                            onClick={() => { setOpenDialogAdd(true) }}
                        >
                            {t("generic.buttons.add")}
                        </ButtonStyled>
                    </div>
                    <div className="d-flex justify-content-center">
                        <DataTable
                            columns={columnsConsecutives}
                            setColumns={setColumnsConsecutives}
                            data={consecutiveData}
                            entityId={"consecutive_id"}
                            entity={`Consecutive`}
                            preferences={myPreferences}
                            namePreferences={"grid-consecutives-companylocations"}
                            nameOrderColumns={"grid-consecutives-companylocations-columns"}
                            isChildren={true}
                            setOpenDialog={setOpenDialogUpdate}
                            setDataRow={setConsecutivePayload}
                        />
                    </div>
                    <DialogAddConsecutive
                        open={openDialogAdd}
                        setOpen={setOpenDialogAdd}
                        comboResources={comboResources}
                        onPost={onPost}
                    />
                    <DialogUpdateConsecutive
                        open={openDialogUpdate}
                        setOpen={setOpenDialogUpdate}
                        comboResources={comboResources}
                        onPut={onPut}
                        defaultConsecutive={consecutivePayload}
                        setConsecutivePayload={setConsecutivePayload}
                    />
                </>
            }
        </>
    )
}